/* eslint-disable no-unused-vars */
import React from 'react';
import {
  ArrowUpRight,
  Boxes,
  ChevronDown,
  GitBranch,
  GitMerge
} from 'lucide-react';
import { StatusChip } from './Avatar';
import { JourneyDetail } from './JourneyDetail';
import { formatRelativeDate } from '../utils/formatters';
import { actionRecommendation } from '../utils/journey-action-copy';
import type { JourneyRadarItem } from '../utils/journey-radar';
import type { WorkJourneyItemRow } from '../api';
import { journeyFocusExpectation } from '../utils/journey-radar';
import {
  workRadarStatusLabel,
  type WorkAttentionLevel
} from '../utils/work-radar';

const ATTENTION_TONE: Record<
  WorkAttentionLevel,
  'success' | 'warn' | 'neutral'
> = {
  critical: 'warn',
  attention: 'warn',
  active: 'neutral',
  complete: 'success',
  closed: 'neutral'
};

interface JourneyResultRowProps {
  item: JourneyRadarItem;
  taskItems: WorkJourneyItemRow[];
  onCreateTaskRelationship: (
    sourceWorkJourneyItemId: string,
    targetWorkJourneyItemId: string
  ) => Promise<boolean>;
  onDeleteTaskRelationship: (relationshipId: string) => Promise<void>;
  projectNames: string[];
  expanded: boolean;
  attentionDetail: string;
  dispatchableStageKeys: Set<string>;
  dispatchingStageKey?: string;
  onToggle: () => void;
  onAcknowledge: (stageKey: string, label: string) => void;
  onDispatch: (stageKey: string, label: string) => void;
}

export function JourneyResultRow({
  item,
  taskItems,
  onCreateTaskRelationship,
  onDeleteTaskRelationship,
  projectNames,
  expanded,
  attentionDetail,
  dispatchableStageKeys,
  dispatchingStageKey,
  onToggle,
  onAcknowledge,
  onDispatch
}: JourneyResultRowProps) {
  const repositories = [
    ...new Set(
      item.journey.items.map((signal) => signal.externalRepo).filter(Boolean)
    )
  ];
  const openableSignal = item.journey.items.find((signal) => signal.url);
  const waitingOnHuman = item.nextAction?.waitingOn === 'human';
  const applicableStages = item.stages.filter(
    (stage) => stage.state !== 'skipped'
  );
  const completedStages = applicableStages.filter(
    (stage) => stage.state === 'success'
  ).length;
  const focusExpectation = journeyFocusExpectation(item.journey);

  return (
    <article
      id={`journey-${item.journey.workJourneyId}`}
      className={`journeyRow journeyRow--${item.attentionLevel}${item.hasFailedSignal ? ' journeyRow--failed' : ''}${item.isStale ? ' journeyRow--stale' : ''}`}
    >
      <div className="journeyRowHeader">
        <button
          type="button"
          className="journeyIdentity journeySummaryButton"
          aria-expanded={expanded}
          onClick={onToggle}
        >
          <div className="journeyTitleLine">
            <strong>{item.journey.title}</strong>
            <StatusChip tone={ATTENTION_TONE[item.attentionLevel]}>
              {workRadarStatusLabel({
                attentionLevel: item.attentionLevel,
                hasFailedSignal: item.hasFailedSignal,
                isStale: item.isStale,
                waitingOnHuman
              })}
            </StatusChip>
          </div>
          <span className="journeyDecisionLine">
            <b>{item.nextAction ? 'Why' : 'Status'}</b>
            {attentionDetail}
          </span>
          {item.nextAction ? (
            <span className="journeyNextLine">
              <b>Next</b>
              {actionRecommendation(item.nextAction)}
            </span>
          ) : null}
          <div className="journeyMeta">
            <span>
              <GitBranch size={13} />
              {repositories.length
                ? `${repositories[0]}${repositories.length > 1 ? ` +${repositories.length - 1}` : ''}`
                : 'Repository unknown'}
            </span>
            {projectNames.length ? (
              <span>
                <GitMerge size={13} />
                {projectNames[0]}
                {projectNames.length > 1 ? ` +${projectNames.length - 1}` : ''}
              </span>
            ) : null}
            {item.journey.taskRefs.length ? (
              <span>
                <Boxes size={13} />
                {item.journey.taskRefs.slice(0, 2).join(', ')}
                {item.journey.taskRefs.length > 2
                  ? ` +${item.journey.taskRefs.length - 2}`
                  : ''}
              </span>
            ) : null}
            <span title={item.journey.lastActivityAt}>
              {formatRelativeDate(item.journey.lastActivityAt)}
            </span>
          </div>
        </button>
        <div className="journeyActions">
          {openableSignal?.url ? (
            <a
              className="iconButton"
              href={openableSignal.url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${openableSignal.title}`}
            >
              <ArrowUpRight size={15} />
            </a>
          ) : null}
          <button
            type="button"
            className="iconButton"
            aria-label={expanded ? 'Collapse journey' : 'Inspect journey'}
            aria-expanded={expanded}
            onClick={onToggle}
          >
            <ChevronDown size={15} />
          </button>
        </div>
      </div>
      <div className="journeyProgressSummary">
        <div className="journeyProgressTrack" aria-hidden="true">
          <span
            style={{
              width: `${applicableStages.length ? Math.round((completedStages / applicableStages.length) * 100) : 0}%`
            }}
          />
        </div>
        <span>
          {completedStages}/{applicableStages.length}
        </span>
        <span>{focusExpectation?.label || 'Complete'}</span>
      </div>
      {expanded ? (
        <JourneyDetail
          journey={item.journey}
          taskItems={taskItems}
          onCreateTaskRelationship={onCreateTaskRelationship}
          onDeleteTaskRelationship={onDeleteTaskRelationship}
          dispatchableStageKeys={dispatchableStageKeys}
          dispatchingStageKey={dispatchingStageKey}
          onAcknowledge={onAcknowledge}
          onDispatch={onDispatch}
        />
      ) : null}
    </article>
  );
}
